import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { ROUTES, generatePath } from '@/constants/routes'
import * as lessonApi from "@/api/lesson"
import LessonWorkbench from "@/components/lesson/LessonWorkbench"
import type { LessonSummary } from "@/types/lesson"
import { format } from "date-fns"
import { zhCN } from "date-fns/locale"
import { Presentation, Trash2, Info } from "lucide-react"

export default function TeacherPage() {
  const { lessonId } = useParams<{ lessonId: string }>()

  if (lessonId) {
    return <LessonWorkbench lessonId={lessonId} />
  }

  return <TeacherHome />
}

function TeacherHome() {
  const navigate = useNavigate()
  const [lessons, setLessons] = useState<LessonSummary[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    lessonApi
      .listLessons()
      .then(setLessons)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const handleDelete = async (id: string) => {
    if (!confirm("确定删除这个微课吗？")) return
    try {
      await lessonApi.deleteLesson(id)
      setLessons((prev) => prev.filter((l) => l.id !== id))
    } catch {
      // ignore
    }
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Presentation className="w-7 h-7 text-blue-600" />
        <h1 className="text-2xl font-semibold">微课工作台</h1>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-3 p-4 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900">
        <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800 dark:text-blue-300 space-y-1">
          <p className="font-medium">课件由线下工具生成</p>
          <p className="text-blue-600 dark:text-blue-400">
            课件内容通过 microcourse-generator skill 生成 JSON，放入 <code className="px-1 py-0.5 rounded bg-blue-100 dark:bg-blue-900">assets/microlessons/</code> 目录，
            运行 <code className="px-1 py-0.5 rounded bg-blue-100 dark:bg-blue-900">sync-lessons.ts</code> 同步到数据库后在此查看。
          </p>
        </div>
      </div>

      {/* Lesson list */}
      <div className="space-y-4">
        <h2 className="text-lg font-medium">我的微课</h2>
        {loading && <div className="text-muted-foreground text-sm">加载中...</div>}
        {!loading && lessons.length === 0 && (
          <div className="text-center text-muted-foreground py-12 border rounded-lg border-dashed">
            暂无微课。请使用 microcourse-generator skill 生成课件后同步。
          </div>
        )}
        {lessons.length > 0 && (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted text-muted-foreground font-medium border-b">
                <tr>
                  <th className="px-4 py-3 text-left">主题</th>
                  <th className="px-4 py-3 text-left">状态</th>
                  <th className="px-4 py-3 text-left">创建时间</th>
                  <th className="px-4 py-3 text-right">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {lessons.map((l) => (
                  <tr key={l.id} className="hover:bg-muted/50">
                    <td className="px-4 py-3 font-medium">{l.topic}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={l.status} />
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {format(new Date(l.createdAt), "yyyy-MM-dd HH:mm", {
                        locale: zhCN,
                      })}
                    </td>
                    <td className="px-4 py-3 text-right space-x-2">
                      {l.status === "ready" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(generatePath(ROUTES.TEACHER_LESSON, { lessonId: l.id }))}
                        >
                          打开
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500"
                        onClick={() => handleDelete(l.id)}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    generating: { label: "生成中", cls: "bg-blue-100 text-blue-800" },
    tts_pending: { label: "语音生成中", cls: "bg-amber-100 text-amber-800" },
    ready: { label: "就绪", cls: "bg-green-100 text-green-800" },
    error: { label: "失败", cls: "bg-red-100 text-red-800" },
  }
  const info = map[status] ?? { label: status, cls: "bg-gray-100 text-gray-800" }
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${info.cls}`}>
      {info.label}
    </span>
  )
}
